import { useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAppStore } from '../store';
import { NotificationService } from '../utils/notificationService';

export const useCrmReminders = (options?: { enableScheduler?: boolean }) => {
  const enableScheduler = options?.enableScheduler !== false;
  const { crmLeads, crmReminders, fetchCrmLeads, fetchCrmReminders, user } = useAppStore();
  const [isLoading, setIsLoading] = useState(true);
  const [sentNotifications, setSentNotifications] = useState<Set<string>>(new Set());
  const sentNotificationsRef = useRef<Set<string>>(new Set());
  const schedulerInstanceRef = useRef<string | null>(null);

  // Eliminamos tracking en localStorage y persistimos en base de datos marcando el recordatorio como completado

  useEffect(() => {
    // Cargar datos inmediatamente, sin esperar al usuario
    const loadData = async (retryCount = 0) => {
      try {
        await Promise.all([
          fetchCrmLeads(),
          fetchCrmReminders()
        ]);
        setSentNotifications(new Set());
        sentNotificationsRef.current = new Set();
      } catch (error: any) {
        // Reintentar hasta 3 veces con delay exponencial
        if (retryCount < 3 && (
          error.message?.includes('Failed to fetch') || 
          error.message?.includes('network') ||
          error.message?.includes('permission denied') ||
          error.message?.includes('Invalid Refresh Token')
        )) {
          const delay = Math.min(1000 * Math.pow(2, retryCount), 5000); // 1s, 2s, 4s
          setTimeout(() => loadData(retryCount + 1), delay);
          return;
        }
      } finally {
        if (retryCount === 0 || retryCount >= 3) {
          setIsLoading(false);
        }
      }
    };

    loadData();

    // Suscripción en tiempo real a cambios en crm_reminders y crm_leads
    const channel = supabase
      .channel('crm-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'crm_reminders' }, () => {
        fetchCrmReminders().catch((e) => console.error('Error refreshing crm_reminders:', e));
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'crm_leads' }, () => {
        fetchCrmLeads().catch((e) => console.error('Error refreshing crm_leads:', e));
      })
      .subscribe();

    return () => {
      try { supabase.removeChannel(channel); } catch {}
    };
  }, [fetchCrmLeads, fetchCrmReminders]);

  useEffect(() => {
    if (!enableScheduler) {
      console.log('[CRM Scheduler] ❌ Scheduler disabled, not starting');
      return; // Solo montar el scheduler si está habilitado
    }

    // Crear una instancia única del scheduler
    const instanceId = `scheduler_${Date.now()}_${Math.random()}`;
    
    // Verificar si ya hay un scheduler ejecutándose
    if (schedulerInstanceRef.current) {
      console.log('[CRM Scheduler] ⚠️ Scheduler already running, skipping new instance');
      return;
    }
    
    schedulerInstanceRef.current = instanceId;
    console.log(`[CRM Scheduler] 🚀 Starting scheduler instance: ${instanceId}`);

    // Verificar recordatorios periódicamente
    const checkReminders = async () => {
      console.log('[CRM Scheduler] ⏰ Check triggered at:', new Date().toISOString());
      
      // Verificar que los datos estén disponibles
      if (!crmReminders || !Array.isArray(crmReminders) || !crmLeads || !Array.isArray(crmLeads)) {
        console.log('[CRM Scheduler] ⏳ CRM data not available yet:', { 
          crmReminders: !!crmReminders, 
          crmLeads: !!crmLeads,
          crmRemindersLength: crmReminders?.length || 0,
          crmLeadsLength: crmLeads?.length || 0
        });
        return;
      }

      console.log(`[CRM Scheduler] 🔍 Checking ${crmReminders.length} CRM reminders...`);
      const now = new Date();
      
      // Procesar recordatorios de forma secuencial para evitar problemas de concurrencia
      for (const reminder of crmReminders) {
        if (reminder.completado) {
          console.log(`[CRM Scheduler] Reminder ${reminder.id} already completed, skipping`);
          continue;
        }

        const reminderDate = new Date(reminder.fecha_recordatorio);
        const lead = crmLeads.find(l => l.id === reminder.lead_id);

        if (!lead) {
          console.warn(`[CRM Scheduler] Lead not found for reminder ${reminder.id}`);
          continue;
        }

        // Crear clave única para evitar duplicados
        const notificationKey = `reminder_${reminder.id}_${reminderDate.toISOString()}`;

        // Verificar si el recordatorio debe ejecutarse AHORA: ventana más amplia de 10 minutos
        const millisDiff = now.getTime() - reminderDate.getTime();
        const isTimeToNotify = millisDiff >= 0 && millisDiff <= 10 * 60 * 1000; // 10 minutos de ventana

        console.log(`[CRM Scheduler] Reminder ${reminder.id}:`, {
          title: reminder.titulo,
          scheduledTime: reminderDate.toISOString(),
          currentTime: now.toISOString(),
          timeDiff: Math.round(millisDiff / 1000 / 60), // minutos
          isTimeToNotify,
          alreadySent: sentNotificationsRef.current.has(notificationKey)
        });

        if (isTimeToNotify && !sentNotificationsRef.current.has(notificationKey)) {
          console.log(`[CRM Scheduler] ⏰ Reminder time reached → ${reminder.titulo} (reminder_id=${reminder.id})`);
          
          try {
            // PRIMERO: Marcar como completado en BD para evitar duplicados
            const { error: updateError } = await supabase
              .from('crm_reminders')
              .update({ completado: true })
              .eq('id', reminder.id)
              .eq('completado', false); // Solo actualizar si no está completado
            
            if (updateError) {
              console.error(`[CRM Scheduler] ❌ Error marking reminder as completed:`, updateError);
              if (updateError.message?.includes('Failed to fetch')) {
                console.log(`[CRM Scheduler] 🔄 Network error, will retry in next cycle`);
              }
              continue; // Saltar este recordatorio si hay error
            }
            
            // Verificar si realmente se actualizó (evitar condiciones de carrera)
            const { data: updatedReminder, error: selectError } = await supabase
              .from('crm_reminders')
              .select('completado')
              .eq('id', reminder.id)
              .single();
            
            if (selectError) {
              console.error(`[CRM Scheduler] ❌ Error verifying reminder completion:`, selectError);
              continue;
            }
            
            if (!updatedReminder?.completado) {
              console.log(`[CRM Scheduler] ⚠️ Reminder ${reminder.id} was already processed by another instance`);
              continue;
            }
            
            console.log(`[CRM Scheduler] ✅ Reminder ${reminder.id} marked as completed in database`);
            
            // ENVIAR notificación solo después de marcar como completado
            await NotificationService.notifyCrmReminderDue(
              reminder.vendedor_id,
              reminder.titulo,
              reminder.descripcion || ''
            );
            console.log(`[CRM Scheduler] ✅ Reminder notification sent successfully for ${reminder.titulo}`);

            // Actualizar estado en memoria
            const newSentNotifications = new Set(sentNotificationsRef.current);
            newSentNotifications.add(notificationKey);
            sentNotificationsRef.current = new Set(newSentNotifications);
            setSentNotifications(newSentNotifications);
            
            console.log(`[CRM Scheduler] ✅ Reminder ${reminder.id} added to sent notifications cache`);
          } catch (error: any) {
            console.error(`[CRM Scheduler] ❌ Error sending reminder notification for ${reminder.titulo}:`, error);
            if (error.message?.includes('Failed to fetch')) {
              console.log(`[CRM Scheduler] 🔄 Network error detected, will retry in next cycle`);
            }
          }
        }
      }
    };

    // Ejecutar inmediatamente
    checkReminders();

    // Configurar intervalo de verificación cada 30 segundos (más estable)
    const interval = setInterval(checkReminders, 30000); // 30 segundos

    console.log('[CRM Scheduler] 🚀 Scheduler started, checking every 30 seconds');

    return () => {
      clearInterval(interval);
      schedulerInstanceRef.current = null;
      console.log(`[CRM Scheduler] 🛑 Scheduler instance ${instanceId} stopped`);
    };
  }, [crmLeads, crmReminders, enableScheduler]);

  return {
    crmLeads: crmLeads || [],
    crmReminders: crmReminders || [],
    isLoading
  };
};
